/**
 * Authentication-related models
 */

import { UserDto } from './user.models';

/**
 * Login Request DTO
 */
export interface LoginRequest {
  email: string;
  password: string;
  rememberMe?: boolean;
  /**
   * Device token for remembered devices (sent via X-Device-Token header, but also accepted in body for convenience).
   */
  deviceToken?: string | null;
}

/**
 * The idle policy the current session runs under, as reported by the API on login, 2FA completion,
 * refresh and init. Mirrors the server's Auth:Policies section so the browser idle timer agrees with it.
 */
export interface SessionPolicy {
  /** Minutes without user activity before the browser logs out. Null when the server did not say (mobile). */
  idleTimeoutMinutes: number | null;
  /** True when the session was created with Remember Me: no idle logout, the refresh token alone bounds it. */
  rememberMe: boolean;
}

/**
 * Login Response DTO
 * In production: AccessToken and RefreshToken are set as httpOnly cookies, NOT in response body.
 * In development: Tokens are also included in response body for cross-protocol support (HTTP frontend -> HTTPS API).
 * Marinas and roles are NOT in login response - fetch via /api/auth/init.
 */
export interface LoginResponse {
  user: UserDto;
  isSystemAdmin: boolean;
  /**
   * Access token (only in development mode, for cross-protocol support).
   * In production, token is only in httpOnly cookie.
   */
  accessToken?: string | null;
  /**
   * Refresh token (only in development mode, for cross-protocol support).
   * In production, token is only in httpOnly cookie.
   */
  refreshToken?: string | null;
  /**
   * Access token expiration date/time (always included, even when token is in httpOnly cookie).
   * Allows frontend to know when token expires for proactive refresh.
   */
  accessTokenExpiresAt?: string | null;
  /**
   * Refresh token expiration date/time (always included, even when token is in httpOnly cookie).
   * Allows frontend to know when refresh token expires.
   */
  refreshTokenExpiresAt?: string | null;
  /**
   * Minutes of inactivity after which this session ends (web only; null for mobile).
   * One hour without Remember Me, the whole session lifetime with it.
   */
  sessionIdleTimeoutMinutes?: number | null;
  /** Whether the session was created with Remember Me. Disables the browser idle timer when true. */
  rememberMe?: boolean | null;
  /**
   * Remember me token (secure token, not password) - stored in httpOnly cookie if RememberMe is true.
   */
  rememberMeToken?: string | null;
  /**
   * Indicates if the user must reset their password (e.g., first-time login with temporary password).
   */
  requiresPasswordReset?: boolean;
  /**
   * Number of failed login attempts remaining before lockout.
   */
  failedAttemptsRemaining?: number | null;
  /**
   * Indicates if the account is locked.
   */
  isLockedOut?: boolean;
  /**
   * Lockout end date/time if account is locked.
   */
  lockoutEnd?: string | null;
  /**
   * Indicates if 2FA is required for this login.
   */
  requiresTwoFactor?: boolean;
  /**
   * 2FA session ID (only present if requiresTwoFactor is true).
   */
  twoFactorSessionId?: string | null;
  /**
   * 2FA session expiry date/time (only present if requiresTwoFactor is true).
   */
  sessionExpiresAt?: string | null;
  /**
   * 2FA code expiry date/time (only present if requiresTwoFactor is true).
   */
  codeExpiresAt?: string | null;
  /**
   * Device token (only present if device was remembered after successful 2FA verification).
   * Client should store this securely (IndexedDB/localStorage for web, Keychain/Keystore for mobile).
   */
  deviceToken?: string | null;
  /**
   * Error message for login failures (e.g., account blocked).
   */
  errorMessage?: string | null;
  /**
   * Error code for specific failure conditions (e.g., rate_limited when 2FA code could not be sent).
   */
  errorCode?: string | null;
  /**
   * UTC timestamp when the rate-limit window expires (only set when errorCode = "rate_limited").
   */
  rateLimitResetAt?: string | null;
  // Note: Marinas and roles are NOT in login response - fetch via /api/auth/init
}

/**
 * Marina Information for Init API response
 */
export interface MarinaInfo {
  id: string;
  name: string;
  isPrimary: boolean;
}

/**
 * Init API Response DTO
 * Returns complete user context including accessible marinas and per-marina roles.
 */
export interface InitResponse {
  user: UserDto;
  isSystemAdmin: boolean;
  accessibleMarinas: MarinaInfo[];
  rolesPerMarina: Record<string, string[]>; // MarinaId -> List of role names (ADMIN, DOCK_MASTER, etc.)
  customerMarinaId?: string | null; // Marina ID for client users (when they don't have accessible marinas)
  customerId?: string | null; // Customer ID for client users (for SignalR subscription to payment updates)
  /** Client-scope portal login with a resolved customer (prefer CustomerMarinaId even when staff marinas exist). */
  hasLinkedPortalCustomer?: boolean;
  /**
   * Minutes of inactivity after which this session ends (web only; null for mobile).
   * One hour without Remember Me, the whole session lifetime with it.
   */
  sessionIdleTimeoutMinutes?: number | null;
  /** Whether the session was created with Remember Me. Disables the browser idle timer when true. */
  rememberMe?: boolean | null;
}

/**
 * Refresh Token Request DTO
 * Note: Refresh token is typically read from httpOnly cookie, not request body.
 */
export interface RefreshTokenRequest {
  refreshToken: string;
}

/**
 * Refresh Token Response DTO
 * Tokens are only returned in response body for mobile/token-based flow (when X-Client: mobile header is present).
 * For web/cookie-based flow, tokens are set as httpOnly cookies and response body tokens are null.
 * Expiration times are always included, even when tokens are in httpOnly cookies.
 */
export interface RefreshTokenResponse {
  /**
   * New access token (nullable - only returned for mobile/token-flow).
   */
  accessToken?: string | null;
  /**
   * New refresh token (nullable - only returned for mobile/token-flow).
   * If token was rotated, this is the new token. If not rotated, this is the current valid token.
   */
  refreshToken?: string | null;
  /**
   * Access token expiration date/time (always included, even when token is in httpOnly cookie).
   * Allows frontend to know when token expires for proactive refresh.
   */
  accessTokenExpiresAt?: string | null;
  /**
   * Refresh token expiration date/time (always included, even when token is in httpOnly cookie).
   * Allows frontend to know when refresh token expires.
   */
  refreshTokenExpiresAt?: string | null;
  /**
   * Minutes of inactivity after which this session ends (web only; null for mobile).
   * One hour without Remember Me, the whole session lifetime with it.
   */
  sessionIdleTimeoutMinutes?: number | null;
  /** Whether the session was created with Remember Me. Disables the browser idle timer when true. */
  rememberMe?: boolean | null;
}

/**
 * Register Request DTO
 */
export interface RegisterRequest {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  staffRole?: number | null; // Display only, not for authorization
  accountType?: number | null;
  isSystemAdmin?: boolean;
}

/**
 * Change Password Request DTO
 */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/**
 * Request Password Reset DTO
 */
export interface RequestPasswordResetRequest {
  email: string;
  /**
   * Portal type: "customer" or "admin". Determines which reset password URL to use.
   * Defaults to "customer" for backward compatibility.
   */
  portalType?: string;
}

/**
 * Confirm Password Reset DTO
 */
export interface ConfirmPasswordResetRequest {
  email: string;
  token: string;
  newPassword: string;
}

/**
 * Send Activation Link Request
 */
export interface SendActivationLinkRequest {
  email: string;
}

/**
 * Activate Account Request
 */
export interface ActivateAccountRequest {
  email: string;
  token: string;
  password: string;
}

/**
 * Admin Reset Password Request
 */
export interface AdminResetPasswordRequest {
  userId: string;
}

/**
 * Two-Factor Authentication Models
 */

/**
 * Two-Factor Method Enum
 */
export enum TwoFactorMethod {
  Email = 1,
  Sms = 2,
}

/**
 * Verify 2FA Request DTO
 */
export interface VerifyTwoFactorRequest {
  sessionId: string;
  code: string;
  rememberDevice?: boolean; // Default to true (user can uncheck for shared devices)
}

/**
 * Verify 2FA Response DTO
 */
export interface VerifyTwoFactorResponse {
  success: boolean;
  errorMessage?: string | null;
  remainingAttempts?: number | null;
  user?: UserDto | null;
  isSystemAdmin?: boolean;
  requiresPasswordReset?: boolean;
  accessToken?: string | null; // Only for mobile clients
  refreshToken?: string | null; // Only for mobile clients
  accessTokenExpiresAt?: string | null; // Enables proactive refresh; required for web (tokens in cookies, expiration in localStorage)
  refreshTokenExpiresAt?: string | null; // Enables proactive refresh; required for web (tokens in cookies, expiration in localStorage)
  deviceToken?: string | null; // Only if RememberDevice was true
  /**
   * Minutes of inactivity after which this session ends (web only; null for mobile).
   * One hour without Remember Me, the whole session lifetime with it.
   */
  sessionIdleTimeoutMinutes?: number | null;
  /** Whether the session was created with Remember Me. Disables the browser idle timer when true. */
  rememberMe?: boolean | null;
}

/**
 * Request 2FA Code Resend Request DTO
 */
export interface RequestTwoFactorCodeRequest {
  sessionId: string;
  method: TwoFactorMethod;
}

/**
 * User Device DTO
 */
export interface UserDeviceDto {
  id: string;
  deviceName?: string | null;
  platform?: number | null; // ClientPlatform enum: 1=Web, 2=iOS, 3=Android, 4=Mobile (OS unknown)
  userAgent?: string | null;
  ipAddress?: string | null;
  lastIpAddress?: string | null;
  lastTwoFactorVerification: string;
  expiresAt: string;
  lastSeenAt: string;
  createdAt: string;
  isRevoked: boolean;
  isCurrentDevice: boolean;
}

/**
 * User Session DTO (for session timeout management)
 */
export interface UserSessionDto {
  id: string;
  refreshTokenId?: string | null;
  deviceName?: string | null;
  platform?: number | null; // ClientPlatform enum: 1=Web, 2=iOS, 3=Android, 4=Mobile (OS unknown)
  userAgent?: string | null;
  ipAddress?: string | null;
  lastIpAddress?: string | null;
  rememberMe: boolean;
  timeoutMinutes: number;
  expiresAt?: string | null; // server sliding idle deadline when set; UI uses lastActivityAt + timeoutMinutes
  createdAt: string;
  lastActivityAt: string;
  isCurrentSession: boolean;
}

/**
 * User Status DTO
 */
export interface UserStatusDto {
  userId: string;
  email: string;
  isActive: boolean;
  isLockedOut: boolean;
  lockoutEnd?: string | null;
  accessFailedCount: number;
  status: string; // "Active", "Blocked", "Inactive"
}

/**
 * Update User Status Request
 */
export interface UpdateUserStatusRequest {
  userId: string;
  isActive: boolean;
  status?: string | null; // "Active", "Inactive", "Blocked" - optional, for explicit status setting
}